import React, { useState, useRef } from "react";
import { useForm } from "react-hook-form";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";
import TextInput from "../../components/TextInput/TextInput.js";
import SignatureController from "./Controller/signature_controller.ts";
import { notification } from "antd";
import FileInput from "../../components/FileInput/FileInput.js";
import SelectInput from "../../components/TextInput/SelectInput";
import MultipleSelectChip from "../../components/TextInput/MultipleSelectChip";
import InfinityEncryptor from "../../components/EncryptDecrypt/InfinityEncryptor.js";
import CustomSeparator from "./../../layouts/AdminLayout/Header/Breadcrumbs.js";
import BasicDateTimePicker from "../../components/DatePicker/BasicDateTimePicker.js";

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const CreateSignature = () => {
  const fileInputRef = useRef(null); // Create a ref for the file input
  const [clearFileInput, setClearFileInput] = useState(false);
  const [clearSelectMultipleInput, setClearSelectMultipleInput] =
    useState(false);
  const userID = localStorage.getItem("userID");

  // notification
  const [api, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = (type, desc, msg) => {
    api[type]({
      message: msg,
      description: desc,
    });
  };
  // encryptor
  const key = "12345";
  const mode = "YMDHI";
  const Encryptor = new InfinityEncryptor(key, mode);
  const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB in bytes

  const required = () => (
    <span style={{ color: "red", marginLeft: "5px", fontSize: "17px" }}>*</span>
  );

  const {
    handleSubmit,
    register,
    formState: { errors },
    control,
    reset,
    setValue,
  } = useForm({
    defaultValues: {
      userID: userID,
      Designation: "",
      FullName: "",
      image: "",
    },
  });

    const [attachment, setAttachment] = useState();
  
    const makeWhiteBackgroundTransparent = (file) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        const reader = new FileReader();
    
        reader.onloadend = () => {
          img.src = reader.result;
        };
    
        reader.onerror = (err) => reject(err);
        reader.readAsDataURL(file);
    
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
    
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);
    
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const data = imageData.data;
    
          // Replace all nearly-white pixels with transparent
          for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
    
            // Tweak this threshold as needed
            const isWhite = r > 240 && g > 240 && b > 240;
    
            if (isWhite) {
              data[i + 3] = 0; // Set alpha to 0 (fully transparent)
            }
          }
    
          ctx.putImageData(imageData, 0, 0);
    
          canvas.toBlob((blob) => {
            resolve(blob);
          }, "image/png");
        };
      });
    };    

    const onSubmit = async (data) => {
      setClearSelectMultipleInput(false);

      const formData = new FormData();
      formData.append("userID", userID);
      formData.append("designation", Encryptor.Encrypt(data.Designation));
      formData.append("full_name", Encryptor.Encrypt(data.FullName));

      if (attachment) {
        try {
          const transparentBlob = await makeWhiteBackgroundTransparent(attachment);
          const transparentFile = new File([transparentBlob], attachment.name, { type: "image/png" });
          formData.append("image", transparentFile);
        } catch (error) {
          console.error("Error processing the image:", error);
          return;
        }
      }
      

      if (Object.keys(errors).length > 0) {
        console.log("Form has errors:", errors);
        return; // Prevent submission if there are errors
      }

      console.log(attachment);

      try {
        SignatureController.createSignature(formData).then((response) => {
          console.log(response);
          if (response?.status === "success") {
            reset();
            setClearFileInput((prev) => !prev);
            setClearSelectMultipleInput(true);
            if (fileInputRef.current) {
              fileInputRef.current.value = ""; // Clear the file input field
            }
            openNotificationWithIcon(
              "success",
              "Your data has been saved successfully!",
              "Success"
            );
          }
        });
      } catch (error) {
        console.error("Error submitting form:", error);
      }
    };

  const onInvalid = (errors) => {
    console.log("Form has errors:", errors);
  };

  return (
    <Box
      sx={{
        display: "flex",
        backgroundColor: "#e8e8e8",
        minHeight: "150vh",
        height: "auto",
        width: "100vw",
      }}
    >
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <DrawerHeader />
        <CustomSeparator pageName="create_signature" />
        {contextHolder}

        <Typography gutterBottom variant="h4" sx={{ color: "text.secondary" }}>
          Create Signature
        </Typography>
        <form
          onSubmit={handleSubmit(onSubmit, onInvalid, {
            shouldFocusError: true,
          })}
        >
          {/* Designation */}
          <Grid container spacing={2} sx={{ p: 2 }}>
            <Grid size={4}>
              <label>Designation{required()}</label>
            </Grid>
            <Grid size={8}>
              <SelectInput
                type="select"
                {...register("Designation", {
                  required: "This field is required",
                })}
                onErrorCheck={
                  errors.designation ? errors.designation.message : ""
                }
                control={control}
              />
            </Grid>
          </Grid>
          {/* Full Name */}
          <Grid container spacing={2} sx={{ p: 2 }}>
            <Grid size={4}>
              <label>Full Name{required()}</label>
            </Grid>
            <Grid size={8}>
              <TextInput
                type="text"
                {...register("FullName", {
                  required: "This field is required",
                })}
                onErrorCheck={errors.FullName ? errors.FullName.message : ""}
                control={control}
              />
            </Grid>
          </Grid>
          {/* Signature Image */}
          <Grid container spacing={2} sx={{ p: 2 }}>
            <Grid size={4}>
              <label>Choose Signature Image{required()}</label>
            </Grid>
            <Grid size={8}>
            <FileInput
              label="Upload Signature Image"
              name="image"
              control={control}
              rules={{ required: "This field is required" }}
              helperText={errors.image && errors.image.message}
              inputRef={fileInputRef} // Assign the ref to the FileInput component
              onClearInput={clearFileInput} // Use the state for clearing
              setValue={setValue} // Pass setValue here
              {...register("image", {
                required: false,
                validate: (fileList) => {
                  if (
                    fileList &&
                    fileList.length > 0 &&
                    fileList[0].size > MAX_FILE_SIZE
                  ) {
                    return "File size exceeds the 20MB limit.";
                  }
                  return true;
                },
                onChange: (e) => {
                  const selectedFile = e.target.value[0]; // Get the selected file
                  setAttachment(selectedFile);
                },
              })}
            />

            </Grid>
          </Grid>

          <Box textAlign="center" sx={{ paddingBottom: "80px" }}>
            <Button
              sx={{
                borderRadius: "20px",
                mt: 4,
                mb: 4,
                // width: "93.4%",
                height: "48px",
                backgroundColor: "#d33333",
                position: "fixed", // Makes the button fixed
                bottom: "16px", // Position the button 16px from the bottom
                transform: "translateX(-50%)", // Aligns the button in the center
                zIndex: 1000, // Ensures the button stays on top of other elements
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)", // Floating shadow
                transition: "all 0.3s ease", // Smooth transition for hover effects

                "&:hover": {
                  backgroundColor: "#b02e2e", // Darker red on hover
                  boxShadow: "0px 8px 20px rgba(0, 0, 0, 0.3)", // Larger shadow on hover
                  transform: "translate(-50%, -5px)", // Moves up slightly on hover
                },
              }}
              variant="contained"
              type="submit"
            >
              <Typography variant="h6">Save</Typography>
            </Button>
          </Box>
        </form>
      </Box>
    </Box>
  );
};

export default CreateSignature;
